/**
 * Additive level light anchors (`scene.lightAnchors`) → atmosphere layers.
 * A landmark (a hideout's shutter leak, a hung lantern) can add warm world-
 * anchored light without forking its biome preset. Invalid anchors are skipped;
 * a level without anchors gets the same layer array back unchanged.
 */
import type { EffectLayer } from '../../../config/atmosphere/effectLayer.types';
import type { GlowBlobSpec } from '../../../config/atmosphere/presets.config';
import { opacityBreathe } from '../../../config/atmosphere/presetBuilders';
import type { SceneLightAnchor } from '../../../shared/SceneTypes';

const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);

function colorOf(color: unknown): number | null {
  if (finite(color) && color >= 0 && color <= 0xffffff) return Math.round(color);
  if (typeof color === 'string' && /^#[0-9a-f]{6}$/i.test(color)) return parseInt(color.slice(1), 16);
  return null;
}

const unitAlpha = (a: unknown, fallback: number): number => (finite(a) && a > 0 && a <= 1 ? a : fallback);

export const LEVEL_GLOW_DEFAULTS = { size: 240, alpha: 0.3, breathe: { periodMs: 3600, min: 0.88, max: 1.08 } } as const;
export const LEVEL_CANDLE_DEFAULTS = {
  baseAlpha: 0.7, flickerAmp: 0.12, hzMin: 0.5, hzMax: 1.1, height: 18, width: 8, haloScale: 4, wobblePx: 0.6,
} as const;

/** Glow and candle layers for a level's valid light anchors (empty when none). */
export function levelLightLayers(anchors: readonly SceneLightAnchor[] | null | undefined): EffectLayer[] {
  if (!Array.isArray(anchors) || anchors.length === 0) return [];
  const blobs: GlowBlobSpec[] = [];
  const candles: Array<EffectLayer & { kind: 'candle' }> = [];
  for (const a of anchors) {
    const color = colorOf(a?.color);
    if (!a || color === null || !finite(a.x) || !finite(a.y)) continue;
    if (a.kind === 'glow') {
      const size = finite(a.size) && a.size > 0 ? a.size : LEVEL_GLOW_DEFAULTS.size;
      blobs.push({ x: a.x, y: a.y, color, size, alpha: unitAlpha(a.alpha, LEVEL_GLOW_DEFAULTS.alpha) });
    } else if (a.kind === 'candle') {
      candles.push({
        kind: 'candle', id: `level_candle_${candles.length}`, anchors: [{ x: a.x, y: a.y }], color,
        ...LEVEL_CANDLE_DEFAULTS, baseAlpha: unitAlpha(a.alpha, LEVEL_CANDLE_DEFAULTS.baseAlpha),
      });
    }
  }
  const { periodMs, min, max } = LEVEL_GLOW_DEFAULTS.breathe;
  const glow: EffectLayer[] = blobs.length
    ? [{ kind: 'glowBlobs', id: 'level_glow_anchors', blobs, modulators: [opacityBreathe(periodMs, min, max)] }]
    : [];
  return [...glow, ...candles];
}

/** Append a level's light anchors to already-resolved layers. */
export function withLevelLightAnchors(layers: EffectLayer[], anchors: readonly SceneLightAnchor[] | null | undefined): EffectLayer[] {
  const extra = levelLightLayers(anchors);
  return extra.length ? [...layers, ...extra] : layers;
}
